import { Injectable, inject, signal } from '@angular/core';
import { SettingsStore } from '../stores/settings.store';
import { UserSettings } from '../models/settings.model';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private store = inject(SettingsStore);
  public fourDauGateway = signal(false);
  // Expose store properties for backward compatibility
  readonly userSettings = this.store.userSettings;
  readonly localeID = this.store.localeID;
  readonly nzi18n = this.store.nzi18n;
  readonly loading = this.store.loading;
  readonly error = this.store.error;
  public inletGateWayValue: any = {};
  public inletDauValue: any = {};
  public inletSensor: any = {};
  public outletGateWayValue = '';
  public outletDauValue = '';
  public outletSensor = '';
  public commonDauInput = '';

  /**
   * Get user settings from API
   * @returns Promise that resolves when settings are loaded
   */
  async getUserSettings(): Promise<UserSettings> {
    await this.store.loadSettings();
    const settings = this.store.userSettings();
    return settings ?? ({} as UserSettings);
  }

  /**
   * Update user settings
   * @param settings - User settings to update
   * @returns Promise that resolves when settings are updated
   */
  async setUserSettings(settings: UserSettings): Promise<UserSettings> {
    await this.store.updateSettings(settings);
    return this.store.userSettings() ?? settings;
  }

  /**
   * Subscribe to settings changes
   * @returns Observable that emits when settings change
   * @deprecated Use userSettings signal with effect() instead for reactive updates
   *
  onSettingsChanged(): Observable<UserSettings> {
    return toObservable(this.store.userSettings).pipe(
      filter((settings): settings is UserSettings => settings !== null),
      map((settings) => settings)
    );
  }

  /**
   * Subscribe to settings changes after get operations
   * @returns Observable that emits when settings are loaded
   * @deprecated Use userSettings signal with effect() instead for reactive updates
   *
  onSettingsChangedAfterGet(): Observable<UserSettings> {
    // Same as onSettingsChanged for signal-based implementation
    return this.onSettingsChanged();
  }
    */

  /**
   * Get locale ID based on current language setting
   * @returns Locale string (e.g., 'en-US', 'de-DE', 'pt-PT')
   */
  getLocaleID(): string {
    return this.store.localeID();
  }

  /**
   * Get ng-zorro i18n configuration based on current language
   * @returns ng-zorro i18n object
   */
  getNZI18n(): any {
    return this.store.nzi18n();
  }

  /**
   * @deprecated Use store.loadSettings() instead
   */
  fetchUserSettings() {
    return this.store.loadSettings();
  }

  /**
   * @deprecated Use store.updateSettings() instead
   */
  updateUserSettings(settings: UserSettings) {
    return this.store.updateSettings(settings);
  }
}
